/**
 * Duplicate-email cluster UI — pick primary row, choose rows to merge, apply cluster action.
 */
import { useEffect, useMemo, useState } from 'react';
import SearchableSelect from './Form/SearchableSelect';
import { batchesAPI } from '../services/api';
import { normalizeApiListResponse } from '../utils/apiListNormalize';
import { formatBatchSelectLabel } from '../domain/enrollment/batchLabels';
import { BATCH_SEARCHABLE_SELECT_PROPS } from '../domain/batch/batchDisplayLabel';
import { matchCatalogBatchId } from '../domain/enrollment/matchCatalogBatch';

const MERGE_COMPARE_FIELDS = [
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'phone', label: 'Phone' },
  { key: 'batch', label: 'Batch' },
  { key: 'course', label: 'Course' },
];

function cellText(value) {
  if (value === undefined || value === null) return '';
  return String(value).trim();
}

export function mergeableFieldLabels(primary, sibling) {
  if (!primary || !sibling || primary.id === sibling.id) return [];
  return MERGE_COMPARE_FIELDS.filter(
    ({ key }) => !cellText(primary[key]) && cellText(sibling[key])
  ).map(({ label }) => label);
}

/** True when at least one ticked row is not the primary (the button label's "ticked"). */
export function canMergeTickedRows(primaryId, mergeIds) {
  if (primaryId == null) return false;
  const pid = String(primaryId);
  for (const id of mergeIds || []) {
    if (String(id) !== pid) return true;
  }
  return false;
}

export default function StagingDuplicateClusterPanel({
  email = '',
  rows = [],
  selectedId = null,
  formBatchId = '',
  onSelectRow,
  onClusterAction,
  acting = false,
  loading = false,
}) {
  const rowIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const primary = useMemo(
    () => rows.find((r) => r.id === selectedId) || rows[0] || null,
    [rows, selectedId]
  );

  const mergeableById = useMemo(() => {
    const map = new Map();
    for (const row of rows) {
      map.set(row.id, mergeableFieldLabels(primary, row));
    }
    return map;
  }, [rows, primary]);

  const [mergeIds, setMergeIds] = useState(() => new Set(rowIds));
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [userPickedBatch, setUserPickedBatch] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setBatchesLoading(true);
    batchesAPI
      .getAll()
      .then((res) => {
        if (!cancelled) setBatches(normalizeApiListResponse(res.data));
      })
      .catch(() => {
        if (!cancelled) setBatches([]);
      })
      .finally(() => {
        if (!cancelled) setBatchesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setUserPickedBatch(false);
  }, [email, rowIds.join('|')]);

  useEffect(() => {
    if (userPickedBatch) return;
    const hints = [formBatchId, primary?.batch, ...rows.map((r) => r.batch)];
    for (const hint of hints) {
      const id = matchCatalogBatchId(batches, hint);
      if (id) {
        setSelectedBatchId(id);
        return;
      }
    }
    setSelectedBatchId('');
  }, [formBatchId, primary?.id, primary?.batch, rowIds.join('|'), batches, userPickedBatch]);

  useEffect(() => {
    setMergeIds(new Set(rowIds));
  }, [rowIds.join('|')]);

  if (loading) {
    return (
      <div className="p-3 rounded-lg border border-indigo-200 bg-indigo-50/60 text-sm text-indigo-900">
        Loading duplicate rows for this email…
      </div>
    );
  }

  if (!rows.length) return null;

  const toggleMerge = (id) => {
    setMergeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const mergeableRowIds = rows.filter((r) => (mergeableById.get(r.id) || []).length).map((r) => r.id);
  const selectMergeable = () => {
    const next = new Set(mergeableRowIds);
    if (primary?.id) next.add(primary.id);
    setMergeIds(next);
  };
  const mergeFromIds = Array.from(mergeIds);
  const mergeEnabled = canMergeTickedRows(primary?.id, mergeIds);
  const tickedBatchLabels = rows
    .filter((r) => mergeIds.has(r.id) && cellText(r.batch))
    .map((r) => cellText(r.batch));
  const uniqueTickedBatches = [...new Set(tickedBatchLabels)];
  const tickedCourseOnly = rows
    .filter((r) => mergeIds.has(r.id) && cellText(r.course) && !cellText(r.batch))
    .map((r) => cellText(r.course));
  const uniqueCourseOnly = [...new Set(tickedCourseOnly)];
  const selectedBatch = batches.find((b) => String(b.id) === String(selectedBatchId));
  const clusterActionOptions = {
    mergeFromIds,
    batchId: selectedBatchId || undefined,
  };

  return (
    <div className="p-3 rounded-lg border border-indigo-300 bg-indigo-50/90 space-y-3">
      <div>
        <h3 className="font-semibold text-indigo-950 text-sm">
          Duplicate email · {rows.length} row{rows.length === 1 ? '' : 's'}
        </h3>
        <p className="text-xs text-indigo-900 mt-1 break-all">
          Email: <span className="font-semibold">{email || '—'}</span>
        </p>
        <p className="text-xs text-indigo-800 mt-1">
          Primary is the student that is saved. Ticked rows with a batch become
          enrollments. Ticked rows with a course but no batch are kept as course
          (no enrollment) on the student. Merge also copies extra profile fields.
        </p>
      </div>

      <div className="overflow-x-auto rounded-md border border-indigo-200 bg-white">
        <table className="w-full text-xs theme-table">
          <thead>
            <tr className="bg-indigo-50 text-left">
              <th className="p-2 whitespace-nowrap">Primary</th>
              <th className="p-2 whitespace-nowrap">Merge</th>
              <th className="p-2">Row</th>
              <th className="p-2">Name</th>
              <th className="p-2">Phone</th>
              <th className="p-2">Batch</th>
              <th className="p-2">Course</th>
              <th className="p-2">Can merge into primary</th>
              <th className="p-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isPrimary = row.id === selectedId;
              const labels = mergeableById.get(row.id) || [];
              const canMerge = labels.length > 0;
              return (
                <tr
                  key={row.id}
                  className={isPrimary ? 'bg-indigo-100/70' : 'hover:bg-indigo-50/50'}
                >
                  <td className="p-2">
                    <input
                      type="radio"
                      name="staging-dup-primary"
                      checked={isPrimary}
                      onChange={() => onSelectRow?.(row.id)}
                      aria-label={`Use ${row.externalKey} as primary`}
                    />
                  </td>
                  <td className="p-2">
                    <input
                      type="checkbox"
                      checked={mergeIds.has(row.id)}
                      onChange={() => toggleMerge(row.id)}
                      aria-label={`Include ${row.externalKey} in merge`}
                    />
                  </td>
                  <td className="p-2 font-medium whitespace-nowrap">
                    <button
                      type="button"
                      className="underline text-indigo-900"
                      onClick={() => onSelectRow?.(row.id)}
                    >
                      {row.externalKey || row.id.slice(0, 8)}
                    </button>
                  </td>
                  <td className="p-2">
                    {[row.firstName, row.lastName].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td className="p-2">{row.phone || '—'}</td>
                  <td className="p-2">{row.batch || '—'}</td>
                  <td className="p-2">{row.course || '—'}</td>
                  <td className="p-2">
                    {isPrimary ? (
                      <span className="text-indigo-700">Primary</span>
                    ) : canMerge ? (
                      <span className="text-emerald-800 font-medium">{labels.join(', ')}</span>
                    ) : (
                      <span className="text-slate-500">Nothing extra</span>
                    )}
                  </td>
                  <td className="p-2">{row.status}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <button
          type="button"
          className="text-xs underline text-indigo-900"
          onClick={selectMergeable}
        >
          Tick rows that can merge
        </button>
        <span className="text-xs text-indigo-800">
          {mergeableRowIds.length} row{mergeableRowIds.length === 1 ? '' : 's'} can fill blanks
          · {mergeFromIds.length} ticked
        </span>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end gap-2">
        <label className="flex-1 min-w-[16rem] text-xs font-medium text-indigo-950">
          Batch for enrollment
          <SearchableSelect
            name="clusterBatchId"
            className="w-full mt-1"
            value={selectedBatchId}
            onChange={(e) => {
              setSelectedBatchId(e.target.value);
              setUserPickedBatch(true);
            }}
            options={batches}
            placeholder={batchesLoading ? 'Loading batches…' : 'Select batch…'}
            disabled={acting || batchesLoading || batches.length === 0}
            {...BATCH_SEARCHABLE_SELECT_PROPS}
            aria-label="Batch for enrollment"
          />
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={acting || !selectedId}
          onClick={() => onClusterAction?.('accept', clusterActionOptions)}
          className="text-sm font-medium px-3 py-2 rounded-md border border-indigo-400 bg-white text-indigo-950 disabled:opacity-50"
        >
          Accept primary row
        </button>
        <button
          type="button"
          disabled={acting || !selectedId || !mergeEnabled}
          onClick={() => onClusterAction?.('merge', clusterActionOptions)}
          className="text-sm font-medium px-3 py-2 rounded-md border border-indigo-400 bg-white text-indigo-950 disabled:opacity-50"
          title={
            mergeEnabled
              ? 'Copy extra fields from ticked rows and keep each ticked row’s batch as an enrollment'
              : 'Tick at least one other row to merge it into the primary'
          }
        >
          Merge ticked into primary
        </button>
        <button
          type="button"
          disabled={acting || !selectedId || (!selectedBatchId && uniqueTickedBatches.length < 1)}
          onClick={() => onClusterAction?.('add_enrollment', clusterActionOptions)}
          className="text-sm font-medium px-3 py-2 rounded-md border border-indigo-400 bg-indigo-700 text-white disabled:opacity-50"
          title={
            uniqueTickedBatches.length || selectedBatch
              ? 'Keep ticked-row batches as enrollments on the primary student'
              : 'Tick a row that has Batch, or select a batch from the dropdown'
          }
        >
          Add enrollment
        </button>
      </div>
      {uniqueTickedBatches.length ? (
        <p className="text-xs text-indigo-800">
          Will keep {uniqueTickedBatches.length} enrollment
          {uniqueTickedBatches.length === 1 ? '' : 's'} from ticked rows:{' '}
          <span className="font-semibold">{uniqueTickedBatches.join(', ')}</span>
          {selectedBatch ? (
            <>
              {' '}
              plus dropdown{' '}
              <span className="font-semibold">{formatBatchSelectLabel(selectedBatch)}</span>
            </>
          ) : null}
        </p>
      ) : selectedBatch ? (
        <p className="text-xs text-indigo-800">
          Will enroll in{' '}
          <span className="font-semibold">{formatBatchSelectLabel(selectedBatch)}</span>
        </p>
      ) : (
        <p className="text-xs text-amber-800">
          {batchesLoading
            ? 'Loading batches…'
            : batches.length === 0
              ? 'No batches loaded. Refresh the page or pick Batch on the form below.'
              : 'Tick rows that have Batch, or select a batch from the dropdown.'}
        </p>
      )}
      {uniqueCourseOnly.length ? (
        <p className="text-xs text-indigo-800">
          Will keep {uniqueCourseOnly.length} course
          {uniqueCourseOnly.length === 1 ? '' : 's'} without enrollment:{' '}
          <span className="font-semibold">{uniqueCourseOnly.join(', ')}</span>
        </p>
      ) : null}
    </div>
  );
}
